import React, { useState, useEffect } from 'react';
import { S3Client, ListObjectsV2Command } from '@aws-sdk/client-s3';
import { S3_CONFIG, supabase } from '../config/supabase';

export default function ImageLibraryModal({ isOpen, onClose, onSelectImage }) {
  const [libraryImages, setLibraryImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  useEffect(() => {
    if (isOpen && libraryImages.length === 0) {
      loadLibraryImages();
    }
  }, [isOpen]);

  const loadLibraryImages = async () => {
    setLoadingImages(true);
    try {
      console.log('Loading library images from Supabase Storage');
      
      // Create S3 client
      const s3Client = new S3Client(S3_CONFIG);
      
      // List objects in the Prayer Group Images folder
      const listCommand = new ListObjectsV2Command({
        Bucket: 'assets',
        Prefix: 'Prayer Group Images/',
      });
      
      const response = await s3Client.send(listCommand);
      console.log(`📋 Found ${response.Contents?.length || 0} objects in storage`);
      
      // Filter for image files and generate public URLs
      const imageUrls = (response.Contents || [])
        .filter(obj => {
          const key = obj.Key || '';
          return key.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        })
        .map(obj => {
          const fileName = obj.Key?.split('/').pop() || '';
          const publicUrl = `https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/${encodeURIComponent(obj.Key)}`;
          
          return {
            name: fileName,
            url: publicUrl,
          };
        });
      
      console.log(`✅ Loaded ${imageUrls.length} prayer images`);
      setLibraryImages(imageUrls);
      
    } catch (error) {
      console.error('❌ Error loading images:', error);
      setLibraryImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageSelect = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
  };

  const handleConfirm = () => {
    if (selectedImageUrl) {
      onSelectImage(selectedImageUrl);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedImageUrl(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" onClick={handleClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 m-0">Choose from Library</h2>
          <button className="bg-transparent border-none text-gray-600 cursor-pointer p-1 rounded hover:bg-gray-100 hover:text-gray-900 transition-all flex items-center justify-center" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingImages ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-[#667eea] rounded-full animate-spin"></div>
              <p className="mt-4 text-base text-gray-600">Loading images...</p>
            </div>
          ) : libraryImages.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-gray-600 text-base">
              <p>No images found in library</p>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
              {libraryImages.map((image) => {
                const isSelected = selectedImageUrl === image.url;
                return (
                  <div
                    key={image.url}
                    className={`relative aspect-video rounded-xl overflow-hidden cursor-pointer border-[3px] transition-all bg-gray-50 ${
                      isSelected
                        ? 'border-[#667eea] shadow-[0_0_0_3px_rgba(102,126,234,0.2)]'
                        : 'border-transparent hover:border-gray-300 hover:translate-y-[-2px] hover:shadow-md'
                    }`}
                    onClick={() => handleImageSelect(image.url)}
                  >
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-full h-full object-cover block"
                      loading="lazy"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-[#667eea]/20 flex items-center justify-center">
                        <div className="w-12 h-12 bg-[#667eea] rounded-full flex items-center justify-center shadow-lg">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
          <button className="px-6 py-3 text-base font-semibold rounded-lg cursor-pointer transition-all border-none bg-transparent text-gray-700 hover:bg-gray-100" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="px-6 py-3 text-base font-semibold rounded-lg cursor-pointer transition-all border-none bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:translate-y-[-1px] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConfirm}
            disabled={!selectedImageUrl}
          >
            Select Image
          </button>
        </div>
      </div>
    </div>
  );
}
